import React from 'react';
import PropTypes from 'prop-types';
import { Search, X } from 'lucide-react';
import { Input } from './Input';
import { cn } from '../../utils/cn';

/**
 * Reusable SearchInput component for consistent styling and behavior across all modules.
 * Enforces correct icon padding (!pl-10 !pr-10) to prevent placeholder/text collision with icons.
 */
export const SearchInput = React.forwardRef(({
  value,
  onChange,
  onClear,
  placeholder = 'Search...',
  className = '',
  containerClassName = '',
  autoFocus = false,
  disabled = false,
  ...props
}, ref) => {
  const handleClear = () => {
    if (onClear) {
      onClear();
    } else if (onChange) {
      onChange({ target: { value: '' } });
    }
  };

  return (
    <div className={cn("relative w-full flex items-center", containerClassName)}>
      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none select-none" />
      <Input
        ref={ref}
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        className={cn(
          "!pl-10 !pr-10 h-10 w-full rounded-lg border-gray-200 bg-white text-sm shadow-sm transition-all",
          "placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
          className
        )}
        {...props}
      />
      {value ? (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none p-1 rounded-full hover:bg-gray-100 transition-colors"
          title="Clear search"
          aria-label="Clear search"
        >
          <X className="w-4 h-4" />
        </button>
      ) : null}
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

SearchInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default SearchInput;
